import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '@/shared/utils/formatBytes';

interface BackupJob {
  id: string;
  status: string;
  completed_at: string | null;
  size_bytes: number | null;
  duration_seconds: number | null;
  backup_id: string | null;
  created_at: string;
  is_restorable?: boolean;
}

interface BackupHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  backupConfigId: string;
  backupConfigName: string;
  onSelectBackup: (jobId: string, completedAt: string) => void;
}

export const BackupHistoryModal = ({
  isOpen,
  onClose,
  backupConfigId,
  backupConfigName,
  onSelectBackup,
}: BackupHistoryModalProps) => {
  const { t, i18n } = useTranslation(['backups', 'common']);
  const [jobs, setJobs] = useState<BackupJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && backupConfigId) {
      fetchBackupHistory();
    }
  }, [isOpen, backupConfigId]);

  const fetchBackupHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/v1/backup-jobs?backup_config_id=${backupConfigId}&status=COMPLETED&page_size=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error();
      }

      const data = await response.json();
      setJobs(data.jobs || []);
    } catch {
      setError(t('historyModal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getTimeAgo = (timestamp: string | null) => {
    if (!timestamp) return '-';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return t('historyModal.time.today');
    if (diffDays === 1) return t('historyModal.time.yesterday');
    if (diffDays < 7) {
      return t(`historyModal.time.daysAgo${diffDays === 1 ? 'One' : 'Other'}`, { count: diffDays });
    }
    const weeks = Math.floor(diffDays / 7);
    if (diffDays < 30) {
      return t(`historyModal.time.weeksAgo${weeks === 1 ? 'One' : 'Other'}`, { count: weeks });
    }
    const months = Math.floor(diffDays / 30);
    return t(`historyModal.time.monthsAgo${months === 1 ? 'One' : 'Other'}`, { count: months });
  };

  const handleSelectBackup = () => {
    if (selectedJobId) {
      const job = jobs.find((j) => j.id === selectedJobId);
      if (job && job.completed_at) {
        onSelectBackup(selectedJobId, job.completed_at);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-surface-card-light dark:bg-surface-card-dark rounded-xl text-left overflow-hidden shadow-elevated transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-surface-border-light dark:border-surface-border-dark">
          {/* Modal header */}
          <div className="bg-surface-2-light dark:bg-surface-2-dark px-4 py-3 sm:px-6 flex justify-between items-center border-b border-surface-border-light dark:border-surface-border-dark">
            <div>
              <h3
                className="text-lg leading-6 font-black text-slate-900 dark:text-white tracking-tight"
                id="modal-title"
              >
                {t('historyModal.title')}
              </h3>
              <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1 font-medium">
                {t('historyModal.configuration')}{' '}
                <span className="font-bold text-slate-900 dark:text-white">{backupConfigName}</span>
              </p>
            </div>
            <button
              type="button"
              aria-label={t('common:actions.close')}
              title={t('common:actions.close')}
              className="min-h-11 min-w-11 bg-transparent rounded-md text-text-subtle-light dark:text-text-subtle-dark hover:text-text-muted-light dark:hover:text-slate-300 focus:outline-none p-1"
              onClick={onClose}
            >
              <span className="sr-only">{t('common:actions.close')}</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal body */}
          <div className="px-4 py-5 sm:p-6 bg-surface-card-light dark:bg-surface-card-dark">
            {error && (
              <div className="mb-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
                <p
                  role="alert"
                  className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark"
                >
                  {error}
                </p>
                <button
                  type="button"
                  onClick={fetchBackupHistory}
                  className="mt-3 min-h-11 rounded-lg px-4 text-sm font-semibold text-state-danger-light underline hover:no-underline dark:text-state-danger-dark"
                >
                  {t('historyModal.retry')}
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                <p className="mt-2 text-sm text-slate-600 dark:text-text-subtle-dark font-medium">
                  {t('historyModal.loading')}
                </p>
              </div>
            ) : error ? null : jobs.filter((job) => job.is_restorable !== false).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('historyModal.empty')}
                  {jobs.length > 0 && (
                    <span className="block mt-2 text-xs text-text-subtle-light dark:text-text-subtle-dark">
                      {t(`historyModal.retainedJobs${jobs.length === 1 ? 'One' : 'Other'}`, {
                        count: jobs.length,
                      })}
                    </span>
                  )}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-600 dark:text-text-subtle-dark mb-4 font-medium">
                  {t('historyModal.instructions')}
                </p>

                {/* Backup list */}
                <div className="border border-surface-border-light dark:border-surface-border-dark rounded-xl overflow-hidden">
                  <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark md:hidden">
                    {jobs
                      .filter((job) => job.is_restorable !== false)
                      .map((job) => (
                        <label
                          key={job.id}
                          className={`flex cursor-pointer gap-3 p-3 transition-colors hover:bg-surface-2-light dark:hover:bg-surface-2-dark ${
                            selectedJobId === job.id
                              ? 'bg-accent-soft-light dark:bg-accent-soft-dark'
                              : 'bg-surface-card-light dark:bg-surface-card-dark'
                          }`}
                        >
                          <input
                            type="radio"
                            name="selectedBackup"
                            aria-label={t('historyModal.selectBackup', {
                              date: formatTimestamp(job.completed_at),
                            })}
                            checked={selectedJobId === job.id}
                            onChange={() => setSelectedJobId(job.id)}
                            className="mt-1 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block break-words text-sm font-semibold text-slate-900 dark:text-slate-200">
                              {formatTimestamp(job.completed_at)}
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-600 dark:text-text-subtle-dark">
                              {getTimeAgo(job.completed_at)}
                            </span>
                            <span className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                              <span className="min-w-0">
                                <span className="block text-text-muted-light dark:text-text-muted-dark">
                                  {t('historyModal.columns.size')}
                                </span>
                                <span className="block font-medium text-slate-900 dark:text-slate-200">
                                  {formatBytes(job.size_bytes)}
                                </span>
                              </span>
                              <span className="min-w-0">
                                <span className="block text-text-muted-light dark:text-text-muted-dark">
                                  {t('historyModal.columns.duration')}
                                </span>
                                <span className="block font-medium text-slate-900 dark:text-slate-200">
                                  {job.duration_seconds
                                    ? t('historyModal.durationSeconds', {
                                        count: job.duration_seconds,
                                      })
                                    : '-'}
                                </span>
                              </span>
                              <span className="col-span-2 min-w-0">
                                <span className="block text-text-muted-light dark:text-text-muted-dark">
                                  {t('historyModal.columns.snapshotId')}
                                </span>
                                <span className="block break-all font-mono text-slate-600 dark:text-text-subtle-dark">
                                  {job.backup_id ? `${job.backup_id.slice(0, 12)}...` : '-'}
                                </span>
                              </span>
                            </span>
                          </span>
                        </label>
                      ))}
                  </div>
                  <div className="hidden overflow-x-auto md:block">
                    <table className="min-w-full divide-y divide-surface-border-light dark:divide-surface-border-dark">
                      <thead className="bg-surface-2-light dark:bg-surface-2-dark">
                        <tr>
                          <th className="px-4 py-3 text-left"></th>
                          <th className="px-4 py-3 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                            {t('historyModal.columns.completedAt')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                            {t('historyModal.columns.timeAgo')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                            {t('historyModal.columns.size')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                            {t('historyModal.columns.duration')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                            {t('historyModal.columns.snapshotId')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface-card-light dark:bg-surface-card-dark divide-y divide-surface-border-light dark:divide-surface-border-dark">
                        {jobs
                          .filter((job) => job.is_restorable !== false)
                          .map((job) => (
                            <tr
                              key={job.id}
                              className={`hover:bg-surface-2-light dark:hover:bg-surface-2-dark cursor-pointer transition-colors ${
                                selectedJobId === job.id
                                  ? 'bg-accent-soft-light dark:bg-accent-soft-dark'
                                  : ''
                              }`}
                              onClick={() => setSelectedJobId(job.id)}
                            >
                              <td className="px-4 py-4 whitespace-nowrap">
                                <input
                                  type="radio"
                                  name="selectedBackup"
                                  aria-label={t('historyModal.selectBackup', {
                                    date: formatTimestamp(job.completed_at),
                                  })}
                                  checked={selectedJobId === job.id}
                                  onChange={() => setSelectedJobId(job.id)}
                                  className="h-4 w-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark"
                                />
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-200">
                                {formatTimestamp(job.completed_at)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-text-subtle-dark">
                                {getTimeAgo(job.completed_at)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-200">
                                {formatBytes(job.size_bytes)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-200">
                                {job.duration_seconds
                                  ? t('historyModal.durationSeconds', {
                                      count: job.duration_seconds,
                                    })
                                  : '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-text-subtle-dark">
                                {job.backup_id ? `${job.backup_id.slice(0, 12)}...` : '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Selected backup info */}
                {selectedJobId && (
                  <div className="mt-4 bg-accent-soft-light dark:bg-accent-soft-dark border border-accent-text-light/20 dark:border-accent-text-dark/20 rounded-xl p-4">
                    <p className="text-sm text-accent-text-light dark:text-accent-text-dark font-medium">
                      <span className="font-black uppercase tracking-wider">
                        {t('historyModal.selected')}:
                      </span>{' '}
                      {t('historyModal.restoreFrom')}{' '}
                      <span className="font-bold">
                        {formatTimestamp(
                          jobs.find((j) => j.id === selectedJobId)?.completed_at || null
                        )}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modal footer */}
          <div className="bg-surface-2-light dark:bg-surface-2-dark px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-surface-border-light dark:border-surface-border-dark">
            <button
              type="button"
              onClick={handleSelectBackup}
              disabled={!selectedJobId || loading}
              className="min-h-11 w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-brand-600 text-base font-black uppercase tracking-wider text-white hover:bg-brand-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('historyModal.restoreSelected')}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="min-h-11 mt-3 w-full inline-flex justify-center rounded-xl border border-border-strong-light dark:border-border-strong-dark shadow-sm px-4 py-2 bg-surface-card-light dark:bg-surface-2-dark text-base font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 hover:bg-surface-2-light dark:hover:bg-surface-border-dark focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 transition-colors"
            >
              {t('historyModal.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
